/* eslint-disable ember/no-computed-properties-in-native-classes */
import EmberObject, { computed, set } from '@ember/object';

import * as Complex from '../../process-data/complex';

export default class BaseActualFilterAmplitude extends EmberObject {
  opAmp = null;
  frequency = null;
  whoAmI = 'app/models/implementation/implementation-common/base-actual-filter-amplitude.js';
  // possibly pre populate sImaginary and sSquared and complexROut
  @computed('frequency') get sImaginary() {
    var frequency = this.frequency;
    var result = frequency * 2 * Math.PI;
    return result;
  }
  @computed('frequency') get sSquared() {
    var frequency = this.frequency;
    var result = -4 * Math.PI * Math.PI * frequency * frequency;
    return result;
  }
  @computed('opAmp.rOut') get complexROut() {
    var rOut = this.opAmp.rOut;
    var result = Complex.createComplex(rOut, 0);
    return result;
  }
  @computed('complexROut', 'complexRParallel', 'opAmp.polesZeroes', 'sImaginary', 'sSquared')
  get complexA() {
    var sSquared = this.sSquared;
    var sImaginary = this.sImaginary;
    var complexRParallel = this.complexRParallel;
    var complexROut = this.complexROut;
    var result = null;
    if (this.opAmp.polesZeroes) {
      result = this._calculateComplexA_PolesAndZeroes(this.opAmp.polesZeroes, sImaginary);
    } else {
      result = this._calculateComplexA(complexRParallel, complexROut, sImaginary, sSquared);
    }
    return result;
  }
  @computed('complexAlphaPlus', 'complexAlphaMinus', 'complexA', 'complexFF')
  get amplitudeNumerator() {
    var complexAlphaPlus = this.complexAlphaPlus;
    var complexAlphaMinus = this.complexAlphaMinus;
    var complexA = this.complexA;
    var complexFF = this.complexFF;
    let result = this._computeAmplitudeNumerator(complexAlphaPlus, complexAlphaMinus, complexA, complexFF);
    return result;
  }
  @computed('complexBetaMinus', 'complexBetaPlus', 'complexA')
  get amplitudeDenominator() {
    var complexOne = Complex.createComplex(1, 0);
    var complexBetaMinus = this.complexBetaMinus;
    var complexBetaPlus = this.complexBetaPlus;
    var complexA = this.complexA;
    var result = complexOne.plus(complexBetaMinus.subs(complexBetaPlus).mult(complexA));
    return result;
  }
  @computed('amplitudeNumerator', 'amplitudeDenominator')
  get complexAmplitude() {
    var amplitudeNumerator = this.amplitudeNumerator;
    var amplitudeDenominator = this.amplitudeDenominator;
    var result = amplitudeNumerator.divide(amplitudeDenominator);
    return result;
  }
  @computed('complexAmplitude') get magnitude() {
    var complexAmplitude = this.complexAmplitude;
    var result = complexAmplitude.magnitude();
    return result;
  }
  @computed('complexAmplitude') get phase() {
    var complexAmplitude = this.complexAmplitude;
    var result = Math.atan2(complexAmplitude.imaginary, complexAmplitude.real);
    return result;
  }
  setFrequency(frequency) {
    set(this, 'frequency', frequency);
  }
  computeMagnitude(complexAlphaPlus, complexFF) {
    let complexAlphaMinus = this.complexAlphaMinus;
    let complexA = this.complexA;
    let amplitudeDenominator = this.amplitudeDenominator;
    let amplitudeNumerator = this._computeAmplitudeNumerator(complexAlphaPlus, complexAlphaMinus, complexA, complexFF);
    let complexAmplitude = this._computeComplexAmplitude(amplitudeNumerator, amplitudeDenominator);
    return complexAmplitude.magnitude();
  }
  _computeAmplitudeNumerator(complexAlphaPlus, complexAlphaMinus, complexA, complexFF) {
    var result = complexAlphaPlus.subs(complexAlphaMinus).mult(complexA).plus(complexFF);
    return result;
  }
  _computeComplexAmplitude(amplitudeNumerator, amplitudeDenominator) {
    var result = amplitudeNumerator.divide(amplitudeDenominator);
    return result;
  }
  _calculateComplexA_PolesAndZeroes(polesZeroes, sImaginary) {
    var i;
    var freq = sImaginary / (2 * Math.PI);

    //Try to keep our complex number declarations to just these three, since each new complex number is quite expensive
    var complexProduct = Complex.createComplex(0, 0);
    var complexDenom = Complex.createComplex(0, 0);
    var complexNum = Complex.createComplex(0, 0);

    //GBW/s
    complexProduct.real = 0;
    complexProduct.imaginary = (-1 * polesZeroes.gbw) / freq;

    //poles:  p[i]/(s+p[i])
    for (i = 0; i < polesZeroes.p.length; i++) {
      complexNum.real = polesZeroes.p[i];
      complexNum.imaginary = 0;
      complexDenom.real = polesZeroes.p[i];
      complexDenom.imaginary = freq;
      complexProduct = complexNum.divide(complexDenom).mult(complexProduct);
    }

    //zeroes:  (s+z[i])/z[i]
    for (i = 0; i < polesZeroes.z.length; i++) {
      complexNum.real = polesZeroes.z[i];
      complexNum.imaginary = freq;
      complexDenom.real = polesZeroes.z[i];
      complexDenom.imaginary = 0;
      complexProduct = complexNum.divide(complexDenom).mult(complexProduct);
    }

    //right half plan zero:  (s-rhpz)/rhpz
    complexNum.real = -1 * polesZeroes.rhpz;
    complexNum.imaginary = freq;
    complexDenom.real = -1 * polesZeroes.rhpz;
    complexDenom.imaginary = 0;
    complexProduct = complexNum.divide(complexDenom).mult(complexProduct);

    return complexProduct;
  }
  _calculateComplexA(complexRParallel, complexROut, sImaginary, sSquared) {
    var GBW = this.opAmp.gbw;
    //we combined the last two terms in the word doc into one term so we could use a lot less complex math (to speed things up)
    var complexTopTerm = Complex.createComplex(8 * Math.PI * Math.PI * GBW * GBW, 0);
    var complexBottomTerm = Complex.createComplex(sSquared, 4 * Math.PI * GBW * sImaginary);
    return complexRParallel.divide(complexROut).mult(complexTopTerm.divide(complexBottomTerm));
  }
}
